// Small presentation and normalisation helpers shared by server and client.

/**
 * Up to two uppercase initials: first letter of the first and of the last word. Falls back to the
 * email's local part, then to "?", so the account-menu trigger always has something to render.
 */
export function initials(name: string | null | undefined, email?: string | null): string {
  const words = (name ?? '')
    .trim()
    .split(/\s+/u)
    .map((word) => word.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter((word) => word.length > 0);
  const first = words[0];
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  if (first) {
    const letters = [...first][0] ?? '';
    const lastLetter = last ? ([...last][0] ?? '') : '';
    return (letters + lastLetter).toLocaleUpperCase();
  }
  const local = (email ?? '').split('@')[0]?.replace(/[^\p{L}\p{N}]/gu, '') ?? '';
  const fromEmail = [...local][0];
  return fromEmail ? fromEmail.toLocaleUpperCase() : '?';
}

/** Emails compare case-insensitively everywhere in Calis; store and match the lowercased form. */
export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** The lowercased domain of an address, or null when the value is not a single-@ address. */
export function emailDomain(email: string): string | null {
  const normalised = normaliseEmail(email);
  const parts = normalised.split('@');
  if (parts.length !== 2) return null;
  const [local, domain] = parts;
  if (!local || !domain) return null;
  return domain;
}

/** Shortens text to at most `max` characters, ending with an ellipsis when cut. */
export function truncate(text: string, max: number): string {
  if (max <= 0) return '';
  const chars = [...text];
  if (chars.length <= max) return text;
  return `${chars.slice(0, Math.max(0, max - 1)).join('')}…`;
}

// --- Well-formed text ---------------------------------------------------------------------------------
//
// Why (production v1.65, 2026-09-28: every brief refused by the API in half a second): a JavaScript
// string is UTF-16, and a cut made in code units can split an emoji's surrogate pair, leaving half of it
// — a lone surrogate — in the text. JSON.stringify writes one as a `\udXXX` escape, which the Messages
// API refuses ("no low surrogate in string"), so one such string anywhere in a request fails the whole
// call, every time. The brief gathers a month of stored text in one request, so one bad string among
// them failed every brief while each meeting's own calls went through. These helpers never make one,
// and repair one that is already stored. Why no `String.prototype.toWellFormed`: the TypeScript lib is
// ES2023, which does not declare it.

/** A high surrogate not followed by a low one, or a low one not preceded by a high one (code units, no `u` flag). */
const LONE_SURROGATE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/** How many lone surrogates the text holds. */
export function countLoneSurrogates(text: string): number {
  return text.match(LONE_SURROGATE)?.length ?? 0;
}

/**
 * The text with every lone surrogate replaced by U+FFFD, the replacement character, as
 * `String.prototype.toWellFormed` does: one code unit for one, so every other position is unchanged.
 * Paired surrogates (an emoji) are kept whole. Idempotent.
 */
export function toWellFormedText(text: string): string {
  return text.replace(LONE_SURROGATE, '�');
}

const isHighSurrogate = (code: number): boolean => code >= 0xd800 && code <= 0xdbff;
const isLowSurrogate = (code: number): boolean => code >= 0xdc00 && code <= 0xdfff;

/** True when a cut before `index` would separate a surrogate pair. */
function splitsPair(text: string, index: number): boolean {
  return index > 0 && index < text.length && isHighSurrogate(text.charCodeAt(index - 1)) && isLowSurrogate(text.charCodeAt(index));
}

/**
 * At most the first `units` UTF-16 code units of the text — one fewer when the cut would split a
 * surrogate pair. Why units, not code points: the limits it serves are `string.length` limits (zod's
 * `.max`, the stores' column bounds), which count units; only the cut is made safe.
 */
export function prefixUnits(text: string, units: number): string {
  if (units <= 0) return '';
  if (units >= text.length) return text;
  return text.slice(0, splitsPair(text, units) ? units - 1 : units);
}

/**
 * The code units from `start` to `end`, never splitting a surrogate pair at either end: a start inside a
 * pair moves past it, an end inside a pair moves before it. Returns the text and the start it used, so a
 * caller holding offsets into the whole text can make them relative to the part.
 */
export function sliceUnits(text: string, start: number, end: number): { text: string; start: number } {
  const from = splitsPair(text, start) ? start + 1 : Math.max(0, start);
  const to = splitsPair(text, end) ? end - 1 : Math.min(text.length, end);
  return { text: to > from ? text.slice(from, to) : '', start: from };
}

/**
 * Text bounded at `max` UTF-16 units, cut at the last space when there is one past `minFraction` of the
 * way, ending with an ellipsis when cut; never splitting a surrogate pair.
 */
export function truncateAtWord(text: string, max: number, minFraction = 0.6): string {
  if (text.length <= max) return text;
  const cut = prefixUnits(text, max - 1);
  const space = cut.lastIndexOf(' ');
  return `${(space > max * minFraction ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Deterministic 32-bit FNV-1a hash. Used for stable per-person avatar colours (design system §14)
 * — never for anything security-relevant.
 */
export function stableHash(value: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/**
 * Returns the path when it is a safe same-origin redirect target, otherwise null.
 * Why: `returnTo` round-trips through the sign-in flow; accepting `//host` or `/\host` would turn
 * the sign-in page into an open redirect.
 */
export function safeReturnPath(candidate: unknown): string | null {
  if (typeof candidate !== 'string') return null;
  if (candidate.length === 0 || candidate.length > 512) return null;
  if (!candidate.startsWith('/') || candidate.startsWith('//') || candidate.startsWith('/\\')) return null;
  // Control characters (including tab/newline, which URL parsers strip) are never legitimate here.
  if (/[ -\\]/u.test(candidate)) return null;
  const base = 'http://calis.invalid';
  let parsed: URL;
  try {
    parsed = new URL(candidate, base);
  } catch {
    return null;
  }
  if (parsed.origin !== base) return null;
  const path = `${parsed.pathname}${parsed.search}${parsed.hash}`;
  // Why: dot-segment normalisation can manufacture what the prefix check refused — `/..//evil.com`
  // becomes the protocol-relative `//evil.com` — so the *output* is checked the same way and, belt and
  // braces, resolved once more against the base to prove it still lands on it.
  if (path.startsWith('//') || path.startsWith('/\\')) return null;
  try {
    if (new URL(path, base).origin !== base) return null;
  } catch {
    return null;
  }
  return path;
}

/** Formats the version for people: `v1.N` (it counts commits; it is not a semantic version). */
export function displayVersion(version: string): string {
  return `v${version}`;
}

/**
 * The app's wording for an account that no longer resolves — the words a deleted account's rows read with (ADR 0167
 * point 4, jeeves repository). The server writes it (audit/audit.ts, routes/admin/shared.ts); the client reads it.
 */
export const DELETED_ACCOUNT_WORDING = 'Deleted account';

/**
 * What the audit log calls a deleted account from its deletion on (owner ruling O4 of 2026-10-05): the wording above and
 * the first eight characters of the account's id, the `<noun> <short id>` form of the delegate view's neutral labels.
 * Why the short id: a stable pseudonym — every row about one deleted account reads alike, and two deleted accounts stay
 * apart when the log is sorted or filtered by actor for an access review (06-modules §3.2). It discloses nothing the row
 * does not already carry: the full id stays on every row as its key. Shared so the client can tell a pseudonym from a
 * name by the very form the server writes (an account it names has no page to open).
 */
export function deletedAccountLabel(subjectId: string): string {
  return `${DELETED_ACCOUNT_WORDING} ${subjectId.slice(0, 8)}`;
}

/**
 * What an admitted colleague is called whose display name stands for nobody — an unverified account, or a name that is
 * an address with nothing before its `@` (owner ruling S5 of 2026-10-02 and ruling 12 of 2026-10-04, ADR 0017): a
 * neutral label, never an address. The server writes it (`displayNameFor`, stores/subjects.ts); shared since the Calis
 * conformity re-audit of 2026-10-10 (D32, D36) so the client can tell the label from a name a person gave and never
 * splice the capitalised label into a sentence. A colleague no longer admitted is not this: see `WITHHELD_COLLEAGUE`.
 */
export const UNNAMED_COLLEAGUE = 'A colleague';

/**
 * What a colleague the resolver no longer admits is called, everywhere a share names them (design-system 04-data §6.4,
 * "an actor no longer admitted is a withheld name, not an anonymous one"): words that say the name is withheld, so it
 * never reads like a person who never gave one. The owner's ruling D33 of 2026-10-10, "A colleague (no longer has
 * access)", for the project's history, extended by his ruling of 2026-10-11 to the shares table and the People tab —
 * "the same words" — so the server writes these on a share's party (`partyName`, routes/projects.ts) and the client the
 * same on the history's withheld actor and colleague (`describeProjectActivity`). One constant, so the two never differ.
 */
export const WITHHELD_COLLEAGUE = 'A colleague (no longer has access)';

/** The same, mid-sentence, as a share entry's object. */
export const WITHHELD_COLLEAGUE_OBJECT = 'a colleague (no longer has access)';

/**
 * Who a sharer's project history says ended a share by deleting an account (the owner's ruling PM-9 of 2026-10-10's
 * rows): the role, never the administrator's name (his ruling of 2026-10-11, "An administrator"). The administrative
 * audit log keeps the name for its readers.
 */
export const AN_ADMINISTRATOR = 'An administrator';
